// Mobile browsers (iOS Safari in particular) can't print a PDF loaded in a
// hidden iframe — they print the host page or nothing. Open the PDF in a new
// tab instead and let the user print from the system viewer.
const isTouchDevice = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

/**
 * Prints a PDF through a hidden iframe. Calls `onDone` once the iframe is gone:
 * after the print dialog closes (afterprint) or, where that event never fires
 * for iframes, when the page regains focus.
 */
export const printPdfUrl = (url: string, onDone?: () => void) => {
  if (isTouchDevice()) {
    window.open(url, '_blank');
    // The new tab still needs the URL; give it time to load before revoking.
    window.setTimeout(() => onDone?.(), 60 * 1000);
    return;
  }

  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = url;

  let cleanedUp = false;
  const cleanup = () => {
    if (cleanedUp) return;
    cleanedUp = true;
    window.removeEventListener('focus', cleanup);
    // Removing the iframe while the dialog is still closing can cancel it.
    window.setTimeout(() => {
      iframe.remove();
      onDone?.();
    }, 100);
  };

  iframe.onload = () => {
    const frameWindow = iframe.contentWindow;
    if (!frameWindow) {
      cleanup();
      return;
    }
    frameWindow.addEventListener('afterprint', cleanup);
    window.addEventListener('focus', cleanup);
    frameWindow.focus();
    frameWindow.print();
  };

  document.body.appendChild(iframe);
};

/** {@link printPdfUrl} for an in-memory PDF; revokes its object URL afterwards. */
export const printPdfBlob = (blob: Blob) => {
  const url = URL.createObjectURL(blob);
  printPdfUrl(url, () => URL.revokeObjectURL(url));
};
